import { MetricCardType } from "@/components/MetricCard/MetricCard.types";
import { Path } from "@/router";
import { LatestAssociateApplication, MembershipApplicationSummary, Person } from "@/types";
import {
  QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID,
  membershipProgressCardNavigation,
  type MembershipProgressLinkOptions
} from "./membershipProgressLinks";
import { isQualifyingLevelComplete } from "./pgm-levels";

type ApplicationTypePrefix = "Affiliate" | "Associate";

type ApplicationStatusLabel =
  | "Application Submitted"
  | "Application In Review"
  | "Application Changes Requested"
  | "Application Approved";

export type ProspectProgramStatusValue =
  | "Not Started"
  | "In Progress"
  | "Qualifier Eligible"
  | "Expired"
  | `${ApplicationTypePrefix} ${ApplicationStatusLabel}`
  | ApplicationStatusLabel;

const APPLICATION_STATUS_BY_TYPE: Record<
  ApplicationTypePrefix,
  Record<ApplicationStatusLabel, ProspectProgramStatusValue>
> = {
  Affiliate: {
    "Application Submitted": "Affiliate Application Submitted",
    "Application In Review": "Affiliate Application In Review",
    "Application Changes Requested": "Affiliate Application Changes Requested",
    "Application Approved": "Affiliate Application Approved"
  },
  Associate: {
    "Application Submitted": "Associate Application Submitted",
    "Application In Review": "Associate Application In Review",
    "Application Changes Requested": "Associate Application Changes Requested",
    "Application Approved": "Associate Application Approved"
  }
};

function formatApplicationType(raw?: string | null): ApplicationTypePrefix | null {
  if (!raw) return null;
  const normalized = raw.toLowerCase().replace(/\s+/g, "_");
  if (normalized.includes("affiliate")) return "Affiliate";
  if (normalized.includes("associate")) return "Associate";
  return null;
}

function withApplicationType(
  type: ApplicationTypePrefix | null,
  label: ApplicationStatusLabel
): ProspectProgramStatusValue {
  if (!type) return label;
  return APPLICATION_STATUS_BY_TYPE[type][label];
}

function mapPostgresApplicationStatus(application: MembershipApplicationSummary): ProspectProgramStatusValue | null {
  const status = application.status?.toLowerCase();
  if (!status || status === "draft") return null;

  const type = formatApplicationType(application.applicationType);

  switch (status) {
    case "under_review":
      return withApplicationType(type, "Application In Review");
    case "changes_requested":
      return withApplicationType(type, "Application Changes Requested");
    case "approved":
      return withApplicationType(type, "Application Approved");
    case "submitted":
      return withApplicationType(type, "Application Submitted");
    case "denied":
    case "rejected":
    case "withdrawn":
    case "cancelled":
      return null;
    default:
      return null;
  }
}

function mapCcmsApplicationStatus(application: LatestAssociateApplication): ProspectProgramStatusValue | null {
  const status = application.status?.toLowerCase();
  if (!status) return null;

  // BackofficeApplication exposes status dates only; treat CCMS rows as Associate.
  if (status === "approved" || status === "elected") {
    return withApplicationType("Associate", "Application Approved");
  }

  if (status === "rejected" || status === "denied") {
    return null;
  }

  // Started / unknown CCMS statuses default to Submitted
  return withApplicationType("Associate", "Application Submitted");
}

function isBackgroundCheckApproved(person: Person): boolean {
  const status = person.prospectProgram?.backgroundCheck?.status?.toUpperCase();
  return status === "APPROVED";
}

function isQualifyingLevelExpired(person: Person): boolean {
  return person.prospectProgram?.qualifyingLevel?.status?.toUpperCase() === "EXPIRED";
}

function isBackgroundCheckExpired(person: Person): boolean {
  return person.prospectProgram?.backgroundCheck?.status?.toUpperCase() === "EXPIRED";
}

function hasInterestForm(person: Person): boolean {
  return !!person.prospectProgram?.membershipInterestForm;
}

function hasEligibleEmployment(person: Person): boolean {
  return !!person.prospectProgram?.eligibleEmployment?.id;
}

function hasQualifierEligibleBand(person: Person): boolean {
  const reasons = [
    person.latestProgram?.band?.createdReason,
    ...(person.bands ?? []).map((band) => band.createdReason)
  ];
  return reasons.some((reason) => reason === "qualifier_eligible");
}

function hasPreQualifierBand(person: Person): boolean {
  const reasons = [
    person.latestProgram?.band?.createdReason,
    ...(person.bands ?? []).map((band) => band.createdReason)
  ];
  return reasons.some((reason) => reason === "pre_qualifier");
}

function incompleteRequirementLabels(person: Person): string[] {
  const missing: string[] = [];
  if (!hasEligibleEmployment(person)) missing.push("Eligible Employment");
  if (!isBackgroundCheckApproved(person)) missing.push("Background Check");
  if (!isQualifyingLevelComplete(person)) missing.push("Qualifying Level Courses");
  return missing;
}

function resolveApplicationStatus(person: Person): ProspectProgramStatusValue | null {
  const postgresApps = person.membershipApplications ?? [];
  if (postgresApps.length > 0) {
    // Newest Postgres application is authoritative; terminal statuses skip CCMS fallback.
    return mapPostgresApplicationStatus(postgresApps[0]);
  }

  const ccmsApp = person.prospectProgram?.latestAssociateApplication;
  if (ccmsApp?.status) {
    return mapCcmsApplicationStatus(ccmsApp);
  }

  return null;
}

export function resolveProspectProgramStatus(person: Person): {
  value: ProspectProgramStatusValue;
  variant?: MetricCardType<Path>["variant"];
  tooltip?: MetricCardType<Path>["tooltip"];
} {
  const applicationStatus = resolveApplicationStatus(person);
  if (applicationStatus) {
    const isApproved = applicationStatus.endsWith("Application Approved");
    const isChanges = applicationStatus.endsWith("Application Changes Requested");
    return {
      value: applicationStatus,
      variant: isApproved ? "success" : isChanges ? "warn" : "info"
    };
  }

  const missing = incompleteRequirementLabels(person);
  const qlExpired = isQualifyingLevelExpired(person);
  const bgcExpired = isBackgroundCheckExpired(person) && !isBackgroundCheckApproved(person);

  if (qlExpired || bgcExpired) {
    return {
      value: "Expired",
      variant: "error",
      tooltip: missing.length
        ? { supportingText: `Still needed: ${missing.join(", ")}` }
        : { supportingText: "A Prospect Program requirement window has expired." }
    };
  }

  if ((isQualifyingLevelComplete(person) && isBackgroundCheckApproved(person)) || hasQualifierEligibleBand(person)) {
    return {
      value: "Qualifier Eligible",
      variant: "success",
      tooltip: missing.length ? { supportingText: `Still needed: ${missing.join(", ")}` } : undefined
    };
  }

  if (
    hasInterestForm(person) ||
    hasPreQualifierBand(person) ||
    person.prospectProgram?.qualifyingLevel ||
    person.prospectProgram?.backgroundCheck ||
    person.prospectProgram?.eligibleEmployment
  ) {
    return {
      value: "In Progress",
      variant: "warn",
      tooltip: missing.length ? { supportingText: `Still needed: ${missing.join(", ")}` } : undefined
    };
  }

  return { value: "Not Started" };
}

export function buildProspectProgramStatusMetric(
  person: Person,
  options: MembershipProgressLinkOptions = {}
): MetricCardType<Path> {
  const { value, variant, tooltip } = resolveProspectProgramStatus(person);

  return {
    label: "Prospect Program Status",
    value,
    iconName: "badge",
    ...(variant && { variant }),
    ...(tooltip && { tooltip }),
    ...membershipProgressCardNavigation(person.pgaId, {
      ...options,
      scrollTo: QUALIFYING_LEVEL_REQUIREMENTS_SCROLL_ID
    })
  };
}
